import { PermissionValues } from '../../auth/permissionValues';
import type { Account } from '../../auth/domain/account';
import type { WorkspaceAsset } from '../domain/workspaceAsset';

export const isAssetReadOnly = (account: any, workspaceAsset: any): boolean => {
  if (!account || !workspaceAsset) {
    return true;
  }

  return !account.hasPermission(PermissionValues.asset.update);
};

const RESTRICTED_PERMISSION = '*.restricted';

/**
 * Mirrors the backend asset-access rule
 * (`resolvers/shared/workspaceAssetAccessFilter.ts` + `accountUtils.ts`):
 * - global admins have access to everything;
 * - everyone else only has access within their `allowedWorkspaces`;
 * - restricted-role users additionally must be assigned to the asset via a
 *   group they belong to, or directly via `accounts[]`.
 */
export const hasAssetAccess = (
  account: Account | null | undefined,
  workspaceAsset: WorkspaceAsset | undefined
): boolean => {
  if (!account || !workspaceAsset) {
    return false;
  }

  if (account.isGlobalAdmin()) {
    return true;
  }

  const assetWorkspaceId = workspaceAsset.workspace?.id;
  const inAllowedWorkspace = (account.allowedWorkspaces ?? []).some(
    (w) => w.id === assetWorkspaceId
  );
  if (!inAllowedWorkspace) {
    return false;
  }

  const isRestricted =
    account.effectivePermissions?.includes(RESTRICTED_PERMISSION) ?? false;
  if (!isRestricted) {
    return true;
  }

  const accountGroupIds = (account.groups ?? []).map((g) => g.id);
  const inGroup = (workspaceAsset.groups ?? []).some((g) =>
    accountGroupIds.includes(g.id)
  );
  const assignedToAccount = (workspaceAsset.accounts ?? []).some(
    (a) => a.id === account.id
  );
  return inGroup || assignedToAccount;
};
